import { Link } from 'react-router-dom';

function PrivacyPolicy() {
  const lastUpdated = "March 2026";
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="text-emerald-200 hover:text-white text-sm mb-4 inline-block">
            ← Back to Home
          </Link>
          <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-emerald-100">Last Updated: {lastUpdated}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              GuapGoals ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you use our 
              personal finance management application and related services.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              By using GuapGoals, you agree to the collection and use of information in accordance with 
              this policy. If you do not agree with our policies and practices, please do not use our services.
            </p>
          </section>

          {/* Zero-Knowledge Security */}
          <section className="bg-emerald-50 rounded-xl p-6 border border-emerald-200">
            <h2 className="text-2xl font-bold text-emerald-900 mb-4 flex items-center gap-2">
              <span>🔒</span> Data Privacy & "Zero-Knowledge" Security
            </h2>
            <p className="text-emerald-800 font-medium text-lg mb-4">
              Your Guap is your business, not ours.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              We built GuapGoals to help you manage your money, not to spy on it. We use a strict 
              "zero-knowledge" architecture for your personal finances.
            </p>
            
            <div className="space-y-4">
              <div className="flex gap-3">
                <span className="text-2xl">1.</span>
                <div>
                  <p className="font-semibold text-gray-900">We cannot see your money.</p>
                  <p className="text-gray-700">
                    Our administrative team and developers do not have access to your specific financial data. 
                    We cannot see your income, your account balances, your individual transactions, your debts, 
                    or the specific details of your savings goals.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <span className="text-2xl">2.</span>
                <div>
                  <p className="font-semibold text-gray-900">We do not store your bank credentials.</p>
                  <p className="text-gray-700">
                    When you link a bank account, you are connecting securely through Plaid (the same 
                    industry-standard security used by Venmo and Chime). Your bank login and passwords 
                    never touch our servers.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <span className="text-2xl">3.</span>
                <div>
                  <p className="font-semibold text-gray-900">We do not sell your data. Period.</p>
                  <p className="text-gray-700">
                    Many "free" budgeting apps make money by selling your spending habits to advertisers. 
                    We don't. Our revenue comes entirely from our transparent subscription plans.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <span className="text-2xl">4.</span>
                <div>
                  <p className="font-semibold text-gray-900">What we do collect.</p>
                  <p className="text-gray-700">
                    To keep the app running and secure, we collect basic account information:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 mt-2 ml-4 space-y-1">
                    <li>Your email address and first name (for login and support)</li>
                    <li>App usage metrics (like your current login streak and whether you have connected a bank account) so we can fix bugs and improve the experience</li>
                    <li>Payment processing is handled safely and securely by Stripe. We never see your full credit card number.</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">Personal Information</h3>
            <p className="text-gray-700 leading-relaxed">
              When you create an account, we collect:
            </p>
            <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1 ml-4">
              <li>Name and email address</li>
              <li>Authentication credentials (encrypted)</li>
              <li>Profile preferences and settings</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">Financial Information</h3>
            <p className="text-gray-700 leading-relaxed">
              To provide our budgeting services, we collect:
            </p>
            <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1 ml-4">
              <li>Income and expense information you manually enter</li>
              <li>Bill amounts, due dates, and payment schedules</li>
              <li>Savings goals and debt information</li>
              <li>Budget categories and spending patterns</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">Bank Account Information (via Plaid)</h3>
            <p className="text-gray-700 leading-relaxed">
              If you choose to link your bank account, we use Plaid Inc. ("Plaid") to securely connect 
              to your financial institution. When you use this feature:
            </p>
            <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1 ml-4">
              <li>Plaid collects your bank login credentials directly—we never see or store them</li>
              <li>We receive only transaction data (merchant names, amounts, dates) to identify recurring bills</li>
              <li>We store a secure access token to fetch updated transaction data</li>
              <li>Account numbers and login credentials are never stored on our servers</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              For more information about Plaid's privacy practices, please visit{' '}
              <a 
                href="https://plaid.com/legal/#end-user-privacy-policy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-emerald-600 hover:text-emerald-700 underline"
              >
                Plaid's End User Privacy Policy
              </a>.
            </p>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed">
              We use the information we collect to:
            </p>
            <ul className="list-disc list-inside text-gray-700 mt-2 space-y-2 ml-4">
              <li>Provide, maintain, and improve our budgeting and financial tracking services</li>
              <li>Automatically detect and categorize your recurring bills and subscriptions</li>
              <li>Send bill reminders and financial insights</li>
              <li>Personalize your experience and provide relevant recommendations</li>
              <li>Communicate with you about updates, security alerts, and support</li>
              <li>Analyze usage patterns to improve our services</li>
              <li>Protect against fraud and unauthorized access</li>
            </ul>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Security</h2>
            <p className="text-gray-700 leading-relaxed">
              We implement industry-standard security measures to protect your information:
            </p>
            <ul className="list-disc list-inside text-gray-700 mt-2 space-y-2 ml-4">
              <li><strong>Encryption:</strong> All data is encrypted in transit (TLS/SSL) and at rest</li>
              <li><strong>Secure Authentication:</strong> We support two-factor authentication (2FA) for account protection</li>
              <li><strong>Access Controls:</strong> Strict access controls limit who can view your data</li>
              <li><strong>No Credential Storage:</strong> We never store your bank login credentials</li>
              <li><strong>Regular Audits:</strong> We regularly review and update our security practices</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              While we strive to protect your information, no method of transmission over the Internet 
              is 100% secure. We cannot guarantee absolute security but are committed to maintaining 
              the highest standards of data protection.
            </p>
          </section>

          {/* Third-Party Services */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Services</h2>
            <p className="text-gray-700 leading-relaxed">
              We work with trusted third-party service providers:
            </p>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">Plaid Inc.</h3>
            <p className="text-gray-700 leading-relaxed">
              We use Plaid to securely connect to your bank accounts. Plaid is a leading financial 
              services company that connects applications to users' bank accounts. Plaid is SOC 2 
              Type II certified and follows strict security protocols.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">Google OAuth</h3>
            <p className="text-gray-700 leading-relaxed">
              We offer Google Sign-In for convenient authentication. When you use this feature, 
              Google shares your basic profile information (name, email) with us according to 
              Google's Privacy Policy.
            </p>

            <p className="text-gray-700 leading-relaxed mt-4">
              We do not sell your personal information to third parties for marketing purposes.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Retention</h2>
            <p className="text-gray-700 leading-relaxed">
              We retain your personal information for as long as your account is active or as needed 
              to provide you services. If you request account deletion, we will delete your personal 
              data within 30 days, except where we are required to retain it for legal or regulatory purposes.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights</h2>
            <p className="text-gray-700 leading-relaxed">
              You have the following rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside text-gray-700 mt-2 space-y-2 ml-4">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data</li>
              <li><strong>Portability:</strong> Export your data in a machine-readable format</li>
              <li><strong>Disconnect:</strong> Unlink your bank account at any time</li>
              <li><strong>Opt-out:</strong> Disable notifications and marketing communications</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              To exercise these rights, please contact us at{' '}
              <a href="mailto:privacy@guapgoals.com" className="text-emerald-600 hover:text-emerald-700 underline">
                privacy@guapgoals.com
              </a>
              {' '}or use the settings within the app.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              GuapGoals is not intended for children under 13 years of age. We do not knowingly collect 
              personal information from children under 13. If you are a parent or guardian and believe 
              your child has provided us with personal information, please contact us immediately.
            </p>
          </section>

          {/* California Privacy Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">California Privacy Rights (CCPA)</h2>
            <p className="text-gray-700 leading-relaxed">
              California residents have additional rights under the California Consumer Privacy Act (CCPA):
            </p>
            <ul className="list-disc list-inside text-gray-700 mt-2 space-y-2 ml-4">
              <li>Right to know what personal information is collected</li>
              <li>Right to know if personal information is sold or disclosed</li>
              <li>Right to say no to the sale of personal information</li>
              <li>Right to equal service and price, even if you exercise your privacy rights</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              <strong>We do not sell your personal information.</strong>
            </p>
          </section>

          {/* Changes to This Policy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes 
              by posting the new Privacy Policy on this page and updating the "Last Updated" date. 
              For significant changes, we will provide additional notice via email or in-app notification.
            </p>
          </section>

          {/* Contact Us */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed">
              If you have questions about this Privacy Policy or our privacy practices, please contact us:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-700"><strong>GuapGoals</strong></p>
              <p className="text-gray-700">Email: <a href="mailto:privacy@guapgoals.com" className="text-emerald-600 hover:text-emerald-700">privacy@guapgoals.com</a></p>
              <p className="text-gray-700">Support: <Link to="/app/contact" className="text-emerald-600 hover:text-emerald-700">Contact Form</Link></p>
            </div>
          </section>

        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center">
          <Link to="/" className="text-emerald-600 hover:text-emerald-700 font-medium">
            ← Back to GuapGoals
          </Link>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
